import {WORLDS} from './levels.js';
import {SECTION,WIDTH} from './physics.js';
import {Illustration,PAINT} from './art.js';
import {BuilderFigure} from './character.js';
import {fallIntensity} from './collapse.js';
import {visibleAltitudeMarks,metres} from './altitude.js';
const TAU=Math.PI*2;
const clamp=(n,a,b)=>Math.max(a,Math.min(b,n));
const random=i=>{const n=Math.sin(i*127.1+311.7)*43758.5453;return n-Math.floor(n);};

export class Renderer {
  constructor(canvas){
    this.canvas=canvas;this.ctx=canvas.getContext('2d',{alpha:false});this.camera=0;this.zoom=1;this.particles=[];this.snaps=[];this.snapKick=null;this.collapse=null;this.fall=0;this.flinch=0;this.shake=0;this.clock=0;this.reducedMotion=false;
    this.resize();
    this.grain=document.createElement('canvas');this.grain.width=256;this.grain.height=256;
    const g=this.grain.getContext('2d'),im=g.createImageData(256,256);
    for(let i=0;i<im.data.length;i+=4){const v=random(i)*255;im.data[i]=im.data[i+1]=im.data[i+2]=v;im.data[i+3]=18;}g.putImageData(im,0,0);
    this.art=new Illustration(this);this.figure=new BuilderFigure(this);this.cameraX=0;this.worldId=0;
  }
  resize(){this.w=innerWidth;this.h=innerHeight;this.dpr=Math.min(devicePixelRatio||1,2);this.canvas.width=Math.round(this.w*this.dpr);this.canvas.height=Math.round(this.h*this.dpr);this.canvas.style.width=this.w+'px';this.canvas.style.height=this.h+'px';this.scale=Math.min(this.h/16.5,this.w/13.5);this.center=this.w*.49;this.art?.reset();}
  reset(){this.camera=0;this.cameraX=0;this.zoom=1;this.particles=[];this.snaps=[];this.snapKick=null;this.collapse=null;this.fall=0;this.flinch=0;this.shake=0;this.hop=null;this.landingTime=null;this.art?.reset();if(this.figure)this.figure.facing=1;}
  point(x,y){return{x:this.center+(x-(this.cameraX||0))*this.scale,y:this.h*.81-(y-this.camera)*this.scale};}
  screenPoint(x,y){const p=this.point(x,y);return{x:this.center+(p.x-this.center)*this.zoom,y:this.h*.5+(p.y-this.h*.5)*this.zoom};}
  path(points,fill,stroke,width=1,close=true){const c=this.ctx;c.beginPath();points.forEach(([x,y],i)=>i?c.lineTo(x,y):c.moveTo(x,y));if(close)c.closePath();if(fill){c.fillStyle=fill;c.fill();}if(stroke){c.strokeStyle=stroke;c.lineWidth=width;c.stroke();}}
  line(x1,y1,x2,y2,color,width=1){const c=this.ctx;c.beginPath();c.moveTo(x1,y1);c.lineTo(x2,y2);c.strokeStyle=color;c.lineWidth=width;c.stroke();}
  circle(x,y,r,fill,stroke,width=1){const c=this.ctx;c.beginPath();c.arc(x,y,r,0,TAU);if(fill){c.fillStyle=fill;c.fill();}if(stroke){c.strokeStyle=stroke;c.lineWidth=width;c.stroke();}}
  text(text,x,y,size,color,align='left',font='sans-serif',weight=400){const c=this.ctx;c.fillStyle=color;c.font=`${weight} ${size}px ${font}`;c.textAlign=align;c.fillText(text,x,y);}
  event(event){
    if(['build','break','brace','fragment','impact'].includes(event.type)&&event.position){
      const lead=event.type==='break'&&(!this.collapse||this.collapse.age>.42);
      const count=event.type==='break'?(lead?64:20):event.type==='fragment'?18:event.type==='build'?5:8;
      for(let i=0;i<count;i++){
        const breaking=event.type==='break',dust=breaking&&i%5===0,n=event.normal||{x:0,y:1},spread=(random(this.clock*99+i)-.5)*(breaking?11:7),pop=(random(i+this.clock)-.25)*5;
        this.particles.push({x:event.position.x,y:event.position.y,vx:breaking?(event.velocity?.x||0)*.7-n.y*spread+n.x*pop:(random(this.clock*99+i)-.5)*1.7,vy:breaking?(event.velocity?.y||0)*.7+n.x*spread+n.y*pop:random(i+this.clock)*3,life:(breaking?.6:.45)+random(i*7)*.8,max:1.4,size:breaking?(dust?11+random(i)*15:3+random(i*13)*(i%9===0?10:5)):1+random(i*13)*3,kind:event.type,dust,bolt:breaking&&!dust&&i%7===0,angle:random(i*6)*TAU,spin:(random(i+21)-.5)*(breaking?26:8)});
      }
      if(this.particles.length>256)this.particles.splice(0,this.particles.length-256);
    }
    if(event.type==='build'||event.type==='walk')this.hop={time:0,dx:event.dx,dy:event.dy,kind:event.type};
    if(event.type==='win')this.landingTime=0;
    if(event.type==='break'){
      this.snaps.push({x:event.position.x,y:event.position.y,angle:Math.atan2(event.normal?.y??1,event.normal?.x??0),age:0});
      if(this.snaps.length>8)this.snaps.shift();
      if(!this.collapse||this.collapse.age>.42){this.snapKick={age:0,direction:event.direction||1};this.collapse={age:0,x:event.position.x,y:event.position.y};}
      this.shake=Math.max(this.shake,9);this.hop=null;
    }
    if(event.type==='impact')this.shake=Math.max(this.shake,Math.min(this.fall>.1?6:3,event.power*.35));
  }
  background(game){this.art.background(game);}
  rock(x,y,width,depth,side=1){this.art.rock(x,y,width,depth,side);}
  symbol(x,y,r,color){const c=this.ctx;c.beginPath();c.arc(x,y,r,-Math.PI*.38,Math.PI*1.38);c.strokeStyle=color;c.lineWidth=1.4;c.stroke();this.line(x-r*.25,y-r*.9,x+r*.55,y-r*.9,color,1.2);this.circle(x,y,1.6,color);}
  lantern(x,y,size=1){
    const c=this.ctx,u=this.scale/55*size;
    const glow=c.createRadialGradient(x,y,1,x,y,u*29);glow.addColorStop(0,'rgba(255,210,122,.3)');glow.addColorStop(.35,'rgba(255,208,119,.09)');glow.addColorStop(1,'rgba(255,208,119,0)');c.fillStyle=glow;c.fillRect(x-u*29,y-u*29,u*58,u*58);
    c.save();c.translate(x,y);c.scale(u,u);
    this.circle(0,-9,3,null,'#334b50',1.1);this.path([[-5,-6],[5,-6],[4,7],[-4,7]],'#2a4148','#a0a78c',.8);
    const glass=c.createLinearGradient(-3,0,3,0);glass.addColorStop(0,'#c08a42');glass.addColorStop(.5,'#fff0ba');glass.addColorStop(1,'#d6a95c');
    c.fillStyle=glass;c.fillRect(-3,-4,6,9);this.line(-4,-6,4,-6,'#c0b389',1.8);this.line(-4,7,4,7,'#6f705d',2);this.line(0,-4,0,5,'#a4783e',.6);c.restore();
  }
  environment(game){
    const c=this.ctx,s=this.scale,base=this.point(0,0),world=this.theme,paint=PAINT[this.worldId];
    this.rock(base.x,base.y,3*s,2.3*s);
    // Handrails, a forgotten cable reel and a hanging survey tag ground the foundation.
    const rx=base.x-1.05*s;
    this.circle(rx,base.y-.25*s,.22*s,'#536363','#879790',2);this.circle(rx,base.y-.25*s,.08*s,'#303f46');
    for(let i=0;i<3;i++)this.circle(rx,base.y-.25*s,(.11+i*.033)*s,null,'#b0a483',.7);
    this.line(rx-.25*s,base.y,rx-.1*s,base.y-.44*s,'#56655f',2);
    this.line(base.x-.75*s,base.y,base.x-.72*s,base.y-.75*s,paint.metal,2);
    this.line(base.x-.72*s,base.y-.75*s,base.x-.49*s,base.y-.75*s,paint.metal,2);
    this.lantern(base.x-.49*s,base.y-.53*s,.85);
    this.line(base.x+.82*s,base.y,base.x+.82*s,base.y-.6*s,'#9a9d84',2);
    this.path([[base.x+.82*s,base.y-.61*s],[base.x+1.2*s,base.y-.5*s],[base.x+.82*s,base.y-.42*s]],'#b68559');
    for(const [index,ledge] of (game.level.obstacles||[]).entries()){
      const p=this.point(ledge.x,ledge.y);
      if(ledge.side===0){
        // Suspended quarry stones have two exposed edges. Reuse the original
        // materials, with recessed lifting cables instead of a prescribed arrow.
        for(const side of [-1,1]){
          const eye=this.point(ledge.x+side*ledge.width*.32,ledge.y);
          const above=this.h*.5+(-30-this.h*.5)/this.zoom;
          this.line(eye.x,eye.y-6,eye.x+side*.28*s,above,'rgba(65,85,89,.3)',1);
          this.line(eye.x+2,eye.y-6,eye.x+side*.28*s+2,above,'rgba(209,218,205,.2)',.7);
        }
      }
      this.rock(p.x,p.y,ledge.width*s,ledge.depth*s,index+game.level.id*4+5);
      if(ledge.side===0){
        // Both lips share the same treatment; the player reads the next gap.
        for(const side of [-1,1]){
          const eye=this.point(ledge.x+side*ledge.width*.32,ledge.y);
          this.line(eye.x-.11*s,eye.y-1,eye.x+.11*s,eye.y-1,paint.metal,4);
          this.circle(eye.x,eye.y-.085*s,.07*s,null,paint.dark,2);
          this.line(eye.x-side*.14*s,eye.y+.12*s,eye.x-side*.14*s,eye.y+.23*s,'#c4b386',1.5);
        }
        if(game.level.world){
          this.line(p.x-ledge.width*s/2,p.y-1,p.x+ledge.width*s/2,p.y-1,paint.metal,4);
          this.line(p.x-ledge.width*s/2,p.y-3,p.x+ledge.width*s/2,p.y-3,paint.rim,1);
        }
        continue;
      }
      const edge=this.point(ledge.x-ledge.side*ledge.width/2,ledge.y);
      // Old survey paint points around each solid lip. Alternating shelves shape
      // the route; their silhouettes are identical to the collision geometry.
      const mark=edge.x+ledge.side*.32*s,y=edge.y+.26*s;
      if(game.level.id<2)this.path([[mark+ledge.side*.08*s,y-.09*s],[mark-ledge.side*.06*s,y],[mark+ledge.side*.08*s,y+.09*s]],null,'#d9bd81',2,false);
      if(game.level.world){
        this.line(edge.x,edge.y-1,edge.x+ledge.side*ledge.width*s,edge.y-1,paint.metal,5);
        this.line(edge.x,edge.y-3,edge.x+ledge.side*ledge.width*s,edge.y-3,paint.rim,1.5);
        for(let i=0;i<7;i++)this.circle(edge.x+ledge.side*(.3+i*.72)*s,edge.y,1.5,'#4a5f67');
        this.line(edge.x+ledge.side*.5*s,edge.y,edge.x+ledge.side*.5*s,edge.y-.56*s,'#8d9995',2);
        this.lantern(edge.x+ledge.side*.5*s,edge.y-.44*s,.58);
      }
    }
    const left=this.point(game.exitMin,game.level.height),right=this.point(game.exitMax,game.level.height);
    const mid=(left.x+right.x)/2;
    this.rock(mid,left.y,right.x-left.x,3.6*s,2);
    // The landing has a thin, unmistakable walkable lip and a weathered lantern post.
    this.line(left.x,left.y-1,right.x,left.y-1,paint.dark,7);
    this.line(left.x,left.y-3,right.x,left.y-3,paint.wood,4);
    for(let i=0;i<Math.floor((right.x-left.x)/(s*.35));i++){
      this.line(left.x+i*s*.35,left.y-5,left.x+i*s*.35,left.y+2,'#435956',1);
      this.circle(left.x+(i+.3)*s*.35,left.y-2,.8,'#586766');
    }
    const lamp=this.point(game.level.exitSide*(game.level.exitInset+.5),game.level.height+1.1);
    this.line(lamp.x,left.y,lamp.x,lamp.y,paint.dark,6);this.line(lamp.x-1,left.y,lamp.x-1,lamp.y,paint.wood,2);
    this.lantern(lamp.x+game.level.exitSide*.25*s,lamp.y+.22*s,1);
    this.line(lamp.x,lamp.y,lamp.x+game.level.exitSide*.25*s,lamp.y+.03*s,paint.dark,2);
    const signX=mid+game.level.exitSide*.8*s;
    this.line(signX,left.y,signX,left.y-.75*s,'#9fa68e',2);
    c.save();c.translate(signX,left.y-.6*s);c.rotate(.035*Math.sin(this.clock*2));c.fillStyle='#738277';c.fillRect(-.32*s,-.22*s,.64*s,.36*s);this.symbol(0,-.05*s,.1*s,'#d4c9a5');c.restore();
    // Windsock tells direction and strength through movement instead of a danger meter.
    const wx=base.x+1.15*s,wy=base.y-1.45*s;
    this.line(wx,base.y,wx,wy,'#a4a58d',2);
    const dx=(game.wind*1.2+.2)*s;
    this.path([[wx,wy],[wx+dx*.5,wy+Math.sin(this.clock*4)*2],[wx+dx,wy+s*.15],[wx+dx*.5,wy+s*.14],[wx,wy+s*.19]],'#b88a62');
    if(!game.fragment){
      const f=this.point(game.level.flag.x,game.level.flag.y),bob=Math.sin(this.clock*2)*3;
      const glow=c.createRadialGradient(f.x,f.y+bob,1,f.x,f.y+bob,s*.65);glow.addColorStop(0,'rgba(255,224,160,.2)');glow.addColorStop(1,'rgba(255,224,160,0)');c.fillStyle=glow;c.fillRect(f.x-s,f.y+bob-s,s*2,s*2);
      this.symbol(f.x,f.y+bob,s*.16,'#ffdda0');
      for(let i=0;i<3;i++){const a=this.clock*.8+i*TAU/3;this.circle(f.x+Math.cos(a)*s*.3,f.y+bob+Math.sin(a)*s*.24,1,'#ffe8b5');}
      this.line(f.x,f.y+bob-s*.2,f.x,f.y+bob-s*.32,'rgba(255,225,165,.5)',1);
      this.line(f.x,f.y+bob+s*.2,f.x,f.y+bob+s*.32,'rgba(255,225,165,.5)',1);
    }
  }
  section(section,connection,game,alpha){
    const c=this.ctx,p=game.pose(section.body,alpha),q=this.point(p.x,p.y),s=this.scale,w=WIDTH*s,h=SECTION*s;
    const visible=this.screenPoint(p.x,p.y);if(visible.y < -h*2 || visible.y>this.h+h*2)return;
    c.save();c.translate(q.x,q.y);c.rotate(-p.a);
    const grow=clamp(section.age/.23,.02,1);
    if(grow<1){
      if(Math.abs(section.direction)>.5){const edge=-Math.sign(section.direction)*w/2;c.translate(edge,0);c.scale(grow,1);c.translate(-edge,0);}
      else{c.translate(0,h/2);c.scale(1,grow);c.translate(0,-h/2);}
    }
    this.art.frame(w,h,section.index);
    if(connection?.braced){
      this.line(-w*.44,-h*.39,w*.44,h*.39,'#453f34',5);
      this.line(-w*.44,-h*.39,w*.44,h*.39,'#d0a558',3);
      this.line(w*.44,-h*.39,-w*.44,h*.39,'#d0a558',3);
      this.circle(0,0,2.8,'#53696b','#edcc87',.8);
    }
    if(connection&&!connection.broken&&connection.stress>.7){
      c.globalAlpha=clamp((connection.stress-.7)*.6,0,.75);
      this.line(-w*.46,h*.45,w*.46,h*.45,'#efb16d',1.5);
    }
    c.restore();
  }
  character(game,alpha,layout){this.figure.draw(game,alpha,layout);}
  fractures(game,alpha){
    const c=this.ctx,s=this.scale;
    for(const connection of game.connections){
      const f=connection.fracture;if(!f)continue;
      // The torn ends stay attached to the two real bodies as they separate.
      for(const [body,local,angle,side] of [[connection.lower,f.lower,f.lowerAngle,1],[connection.body,f.upper,f.upperAngle,-1]]){
        const p=game.pose(body,alpha),ca=Math.cos(p.a),sa=Math.sin(p.a),q=this.point(p.x+local.x*ca-local.y*sa,p.y+local.x*sa+local.y*ca);
        const screenY=this.h*.5+(q.y-this.h*.5)*this.zoom;if(screenY<-30||screenY>this.h+30)continue;
        c.save();c.translate(q.x,q.y);c.rotate(-p.a-angle);c.scale(side,1);
        const half=f.span*s*.43,tooth=s*.075;
        this.path([[-tooth,-half],[tooth*.7,-half*.82],[-tooth*.2,-half*.48],[tooth*1.2,-half*.21],[0,half*.12],[tooth*.85,half*.47],[-tooth*.25,half*.63],[tooth*.4,half],[-tooth,half]],'#dfbd7f','#30464b',1);
        this.line(-tooth*.2,-half*.78,-tooth*.45,half*.8,'#786343',.8);c.restore();
      }
    }
    for(const snap of this.snaps){
      const q=this.point(snap.x,snap.y),fade=1-snap.age/.26;
      if(fade<=0)continue;
      c.save();c.translate(q.x,q.y);c.rotate(-snap.angle);c.globalAlpha=fade;
      const reach=s*(.38+snap.age*3.8);
      // A single local fracture accent, never a full-screen flash or countdown.
      this.path([[0,-reach],[-3,-reach*.45],[3,0],[-2,reach*.35],[0,reach]],null,'#fff0ca',3.4*fade,false);
      for(const side of [-1,1]){this.line(5,side*reach*.5,11,side*reach*1.3,'#dfc080',fade*1.5);this.line(-6,side*reach*.7,-10,side*reach*1.5,'#f5dfb1',fade);}
      c.restore();
    }
  }
  draw(game,alpha,dt,mode){
    this.clock+=dt;this.worldId=game.level.world;this.theme=WORLDS[game.level.world];if(this.hop)this.hop.time+=dt;
    const descent=fallIntensity(game);
    this.fall=game.status==='won'?0:this.fall+(descent-this.fall)*(1-Math.exp(-dt*18));
    if(this.fall>.2)this.hop=null;
    if(this.collapse){this.collapse.age+=dt;if(this.collapse.age>1.6)this.collapse=null;}
    this.flinch=this.collapse&&game.status==='playing'?Math.max(0,1-this.collapse.age/.95)*.85:0;
    if(this.landingTime!==null&&this.landingTime!==undefined)this.landingTime+=dt;
    let desired=Math.max(0,game.height-5.9);
    const focus=!this.reducedMotion&&game.status==='playing'&&this.collapse?clamp((1.15-this.collapse.age)/.4,0,1):0;
    const fit=focus?clamp(this.h*.65/(this.scale*(Math.abs(game.height-this.collapse.y)+4)),.68,1):1;
    this.zoom+=((1+(fit-1)*focus)-this.zoom)*(1-Math.exp(-dt*(focus?16:5)));
    if(focus){
      const middle=(game.height+this.collapse.y)/2;
      const framed=Math.max(0,middle-(this.h*.31+this.h*.025/this.zoom)/this.scale);
      desired+=(framed-desired)*focus;
    }
    const fallingCamera=!this.reducedMotion&&this.fall>.15&&desired<this.camera;
    const follow=focus?14:fallingCamera?2.3:4.5;
    this.camera+=(desired-this.camera)*(1-Math.exp(-dt*follow));
    // Let the player visibly drop through the shot, but retain space below the
    // builder for a rescue. Camera effects never delay simulation or input.
    if(fallingCamera)this.camera=Math.min(this.camera,Math.max(0,game.height-2.8));
    const playerX=game.player.getPosition().x,desiredX=mode==='title'?0:playerX-clamp(playerX,-2.8,2.8);
    this.cameraX+=(desiredX-this.cameraX)*(1-Math.exp(-dt*3));
    this.shake*=Math.exp(-dt*9);
    if(this.snapKick){this.snapKick.age+=dt;if(this.snapKick.age>.42)this.snapKick=null;}
    for(const snap of this.snaps)snap.age+=dt;this.snaps=this.snaps.filter(snap=>snap.age<.26);
    const c=this.ctx;c.setTransform(this.dpr,0,0,this.dpr,0,0);
    this.background(game);
    c.save();
    // Transform the world drawing, not the cached artwork. A pullback exposes
    // low failed joints without rebuilding textures or moving physical bodies.
    c.translate(this.center,this.h*.5);c.scale(this.zoom,this.zoom);c.translate(-this.center,-this.h*.5);
    if(!this.reducedMotion){
      const kick=this.snapKick?(1-this.snapKick.age/.42)**2*Math.cos(this.snapKick.age*24)*Math.min(22,this.scale*.4):0;
      c.translate(Math.sin(this.clock*71)*this.shake+kick*(this.snapKick?.direction||1),Math.cos(this.clock*59)*this.shake*.6+kick*.3);
    }
    this.environment(game);
    for(let i=0;i<game.sections.length;i++)this.section(game.sections[i],game.connections[i],game,alpha);
    this.fractures(game,alpha);
    // Lightweight construction preview shows exactly what the held build direction will change.
    if(mode==='playing'&&game.playerJoint){
      const preview=game.placement(game.direction,false,game.buildVertical),q=this.point(preview.p.x,preview.p.y);
      c.save();c.translate(q.x,q.y);c.rotate(-preview.a);c.strokeStyle=preview.blocked?'rgba(245,157,119,.8)':'rgba(248,225,173,.6)';c.lineWidth=1.5;c.setLineDash([3,5]);c.strokeRect(-WIDTH*this.scale/2,-SECTION*this.scale/2,WIDTH*this.scale,SECTION*this.scale);c.restore();
      if(game.blocked&&preview.blocked&&q.y>125&&q.y<this.h-160){
        const message=preview.blocked==='rock'?'SOLID ROCK · BACK OUT OR GO AROUND':preview.blocked==='frame'?'FRAME IN THE WAY · TRY ANOTHER PATH':'LET THE TOWER SETTLE';
        c.fillStyle='rgba(29,49,56,.88)';c.fillRect(q.x-126,q.y-SECTION*this.scale/2-34,252,24);
        this.text(message,q.x,q.y-SECTION*this.scale/2-18,10,'#ffe0b5','center');
      }
    }
    const figurePose=this.figure.layout(game,alpha);
    this.character(game,alpha,figurePose);
    for(const p of this.particles){
      p.life-=dt;p.x+=p.vx*dt;p.y+=p.vy*dt;p.vy-=(p.kind==='fragment'||p.dust?1.8:9)*dt;p.angle+=p.spin*dt;
      if(p.dust){p.vx*=Math.exp(-dt*2);p.vy*=Math.exp(-dt*2);}
      const q=this.point(p.x,p.y);c.save();c.translate(q.x,q.y);c.rotate(p.angle);c.globalAlpha=clamp(p.life/.4,0,1);
      c.fillStyle=p.kind==='fragment'?'#ffe0a3':p.kind==='break'?PAINT[this.worldId].wood:'#dfc69a';
      if(p.kind==='break'){
        const unit=Math.min(1.3,this.scale/55);c.scale(unit,unit);
        if(p.dust){c.globalAlpha*=.15;c.scale(1,.6);this.circle(0,0,p.size*(1.65-p.life*.5),'#cfbc94');}
        else if(p.bolt){this.circle(0,0,p.size*.42,'#79918f','#273e46',.8);this.line(-p.size*.25,0,p.size*.25,0,'#d7d9bf',.8);}
        else{this.path([[-p.size,0],[p.size*.75,-p.size*.23],[p.size*.3,p.size*.15],[p.size,p.size*.35],[-p.size*.65,p.size*.4]],PAINT[this.worldId].wood);this.line(-p.size*.5,.5,p.size*.6,.5,'#f2d193',.6);}
      }
      else if(p.kind==='fragment')this.path([[0,-p.size],[p.size*.45,0],[0,p.size],[-p.size*.45,0]],'#ffe8b3');
      else c.fillRect(0,0,p.size,p.size*.4);c.restore();
    }
    this.particles=this.particles.filter(p=>p.life>0);c.globalAlpha=1;
    c.restore();
    // The side ruler reports height; it deliberately makes no promise about structural safety.
    if(mode==='playing'){
      const compact=this.w<700,rx=this.w-(compact?30:44),ruler=visibleAltitudeMarks(this,game.level);
      c.save();
      const railWidth=compact?92:164;
      const shade=c.createLinearGradient(rx-railWidth,0,this.w,0);shade.addColorStop(0,'rgba(16,38,46,0)');shade.addColorStop(.18,compact?'rgba(16,38,46,.34)':'rgba(16,38,46,.72)');shade.addColorStop(1,compact?'rgba(16,38,46,.68)':'rgba(16,38,46,.92)');
      c.fillStyle=shade;c.fillRect(rx-railWidth,0,this.w-rx+railWidth,this.h);
      this.line(rx,ruler.top,rx,ruler.bottom,'#e7bf73',compact?3:4);
      for(const mark of ruler.marks){
        const {altitude,y,major}=mark;
        this.line(rx-(major?(compact?31:39):(compact?12:15)),y,rx,y,major?'#f3d7a0':'rgba(240,230,203,.72)',major?(compact?3:4):1.5);
        if(major){c.shadowColor='#102b34';c.shadowBlur=6;this.text(`${metres(altitude)} m`,rx-(compact?40:50),y+6,compact?17:20,'#fff1d1','right','Arial',700);c.shadowBlur=0;}
      }
      c.restore();
      if(game.height<game.level.height-5&&this.screenPoint(0,game.level.height).y<88){
        const label=`↑  ${Math.ceil(game.level.height-game.height)} m to the landing`,x=compact?this.w*.37:this.w/2,y=compact?174:97;
        c.font='700 12px Arial';const width=c.measureText(label).width+22;
        c.fillStyle='rgba(24,47,57,.78)';c.fillRect(x-width/2,y-15,width,22);
        this.text(label,x,y,12,'#f4e4bd','center','Arial',700);
      }
    }
    // Surface texture lives on the illustrated objects; keep the air clear.
    const vignette=c.createRadialGradient(this.w*.5,this.h*.45,this.h*.25,this.w*.5,this.h*.45,this.h*.85);vignette.addColorStop(0,'transparent');vignette.addColorStop(1,'rgba(14,33,41,.22)');c.fillStyle=vignette;c.fillRect(0,0,this.w,this.h);
  }
}
